/**
 * @file EmployeesPage.tsx
 * @brief Company Admin → Employees directory + Enterprise seat assign.
 *
 * @author DES
 * @date 19-06-2026
 */

import { HintBubble, HintIcon } from "@/components/ui/HintPopover";
import { useHintPopover } from "@/components/ui/useHintPopover";
import { DockActionButton, PageDock, pageWithDockClass } from "@/components/ui/PageDock";
import { EmptyState } from "@/components/feedback/EmptyState";
import { useMemo, useState } from "react";
import { LoaderCircle, Plus, UserRoundPlus } from "lucide-react";
import { useLoadingSkeleton } from "@/shared/hooks/useLoadingSkeleton";
import type { ColumnDef } from "@tanstack/react-table";
import { Switch } from "@/components/ui/Switch";
import { useOpenCreateFromQuery } from "@/shared/hooks/useOpenCreateFromQuery";
import { DataTable } from "@/components/data/DataTable";
import { Badge } from "@/components/ui/Badge";
import { Avatar } from "@/components/ui/Avatar";
import {
  formatFullName,
  formatRelative,
  getInitials,
} from "@/shared/utils/formatters";
import { LoginLocationPill } from "@/shared/components/LoginLocationPill";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import {
  useAssignEnterpriseSeat,
  useCompanyEnterpriseStatus,
  useEmployeesList,
  useRevokeEnterpriseSeat,
} from "./api";
import type { CompanyEmployee } from "./schemas";
import { CreateEmployeeDialog } from "./components/CreateEmployeeDialog";
import { EditEmployeeDialog } from "./components/EditEmployeeDialog";
import { EmployeeLoginHistoryDialog } from "./components/EmployeeLoginHistoryDialog";
import { EmployeeRowActions } from "./components/EmployeeRowActions";
import { MemberDeletionRequests } from "@/features/account-deletion/MemberDeletionRequests";
import {
  GridBand,
  GridBlock,
  GridCell,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";

export function EmployeesPage() {
  const [createOpen, setCreateOpen] = useState(false);
  // The phone's tips icon (DES 05-10-2026), in web words.
  const tips = useHintPopover("company_admin_team_tips");
  const [editTarget, setEditTarget] = useState<CompanyEmployee | null>(null);
  const [historyTarget, setHistoryTarget] = useState<CompanyEmployee | null>(
    null,
  );
  useOpenCreateFromQuery(setCreateOpen);
  const list = useEmployeesList();
  const listSkeleton = useLoadingSkeleton("employees", list.isLoading, list.data?.length);
  const enterprise = useCompanyEnterpriseStatus();
  const assignSeat = useAssignEnterpriseSeat();
  const revokeSeat = useRevokeEnterpriseSeat();

  const enterpriseEnabled = Boolean(enterprise.data?.plan);
  const seatBusyId =
    assignSeat.isPending || revokeSeat.isPending
      ? ((assignSeat.variables as string | undefined) ??
        (revokeSeat.variables as string | undefined) ??
        null)
      : null;

  const onToggleSeat = async (employee: CompanyEmployee) => {
    try {
      if (employee.enterprise_seat) {
        await revokeSeat.mutateAsync(employee.id);
        toast({ title: "Enterprise seat removed" });
      } else {
        await assignSeat.mutateAsync(employee.id);
        toast({ title: "Enterprise seat assigned" });
      }
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Seat update failed";
      toast({ title: "Could not update seat", description: message });
    }
  };

  const columns = useMemo<ColumnDef<CompanyEmployee>[]>(
    () => [
      {
        id: "name",
        header: "Employee",
        cell: ({ row }) => {
          const u = row.original;
          const name = formatFullName(u.first_name, u.last_name);
          return (
            <div className="flex items-center gap-3">
              <Avatar
                src={u.profile_image_url}
                initials={getInitials(u.first_name, u.last_name)}
                gender={u.gender}
                alt={name}
                size="md"
              />
              <div className="min-w-0">
                <p className="truncate font-medium">{name}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {u.email}
                </p>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "role",
        header: "Role",
        cell: ({ row }) => (
          <Badge variant={row.original.role === "admin" ? "info" : "outline"}>
            {row.original.role === "admin" ? "Admin" : "User"}
          </Badge>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => {
          const s = row.original.status;
          const variant =
            s === "active"
              ? "success"
              : s === "suspended"
                ? "warning"
                : "destructive";
          return <Badge variant={variant}>{s}</Badge>;
        },
      },
      ...(enterpriseEnabled
        ? ([
            {
              id: "enterprise_seat",
              header: "Enterprise seat",
              cell: ({ row }) => {
                const u = row.original;
                const seated = Boolean(u.enterprise_seat);
                const busy = seatBusyId === u.id;
                return (
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={seated}
                      disabled={busy}
                      title={
                        seated
                          ? "Remove from shared pool"
                          : "Assign shared MI+Notes pool seat"
                      }
                      aria-label={
                        seated
                          ? `Revoke Enterprise seat for ${u.email}`
                          : `Assign Enterprise seat for ${u.email}`
                      }
                      onCheckedChange={() => void onToggleSeat(u)}
                    />
                    {busy ? (
                      <LoaderCircle className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                    ) : null}
                  </div>
                );
              },
            } satisfies ColumnDef<CompanyEmployee>,
          ] as ColumnDef<CompanyEmployee>[])
        : []),
      {
        id: "last_login_location",
        header: "Location",
        cell: ({ row }) => {
          const u = row.original;
          return (
            <LoginLocationPill
              country={u.last_login_country}
              region={u.last_login_region}
              city={u.last_login_city}
            />
          );
        },
      },
      {
        accessorKey: "created_at",
        header: "Joined",
        cell: ({ row }) =>
          row.original.created_at
            ? formatRelative(row.original.created_at)
            : "—",
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <EmployeeRowActions
            employee={row.original}
            onEdit={() => setEditTarget(row.original)}
            onViewHistory={() => setHistoryTarget(row.original)}
          />
        ),
      },
    ],
    [enterpriseEnabled, seatBusyId],
  );

  const ent = enterprise.data;

  return (
    <div className={gridPageBleedClass(`pb-6 ${pageWithDockClass}`)}>
      <GridBlock>
        <GridBand className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold tracking-tight">Employees</h2>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              Invite and manage your company team.
            </p>
          </div>
          <HintIcon open={tips.open} onClick={tips.toggle} anchorRef={tips.anchorRef} />
          {tips.open ? (
            <HintBubble
              lines={[
                "Use ⋯ on a person to edit them, see their logins, resend the welcome email or suspend them.",
              ]}
              onClose={tips.close}
              anchorRef={tips.anchorRef}
              className="basis-full"
            />
          ) : null}
        </GridBand>

        {/* Members who asked to be deleted (B7, 22-10-2026). */}
        <MemberDeletionRequests />

        {ent?.plan ? (
          <GridBand>
            <p className="font-medium text-sm">Enterprise shared pool</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Seats {ent.seatsUsed}/{ent.plan.seatLimit}
              {" · "}
              Minutes {ent.minutesUsed}/{ent.plan.includedMinutes}
              {ent.enabled ? " · Live" : " · Outside period / inactive"}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Assigned employees draw Meeting Intel + Notes from the shared pool.
              Rates are set by Verblizr.
            </p>
          </GridBand>
        ) : null}

        <GridCell padded={false} className="border-b-0">
          {/* Loading: the table's skeleton rows if there were employees last
              time, else blank until the list or its empty state. */}
          {list.isLoading ? (
            listSkeleton ? (
              <DataTable variant="cell" columns={columns} data={[]} isLoading rowKey={(e) => e.id} />
            ) : null
          ) : list.isError ? (
            <div className="p-6 text-center text-sm text-destructive">
              Couldn't load employees.
            </div>
          ) : list.data?.length === 0 ? (
            <EmptyState
              shape="person"
              title="No employees yet"
              description="Add your first team member to get started."
              action={
                <button type="button" onClick={() => setCreateOpen(true)} className="inline-flex h-12 items-center gap-2 rounded-full bg-brand px-6 text-[15px] font-semibold text-white transition-colors hover:bg-brand/90">
                  <UserRoundPlus className="h-5 w-5" />
                  Add employee
                </button>
              }
            />
          ) : (
            <DataTable
              variant="cell"
              columns={columns}
              data={list.data ?? []}
              rowKey={(e) => e.id}
            />
          )}
        </GridCell>
      </GridBlock>

      {/* The page's action, docked at the bottom (DES 01-10-2026). */}
      <PageDock>
        <DockActionButton icon={<Plus className="h-5 w-5" strokeWidth={2} />} onClick={() => setCreateOpen(true)}>
          Add employee
        </DockActionButton>
      </PageDock>

      <CreateEmployeeDialog open={createOpen} onOpenChange={setCreateOpen} />
      <EditEmployeeDialog
        employee={editTarget}
        open={Boolean(editTarget)}
        onOpenChange={(open) => {
          if (!open) setEditTarget(null);
        }}
      />
      <EmployeeLoginHistoryDialog
        employee={historyTarget}
        open={Boolean(historyTarget)}
        onOpenChange={(open) => {
          if (!open) setHistoryTarget(null);
        }}
      />
    </div>
  );
}
